// CORE-496 — home-page onboarding tour: the spotlight layer (web only).
//
// Dims the page with a "hole" over the active step's anchor block and draws a
// highlight ring around it. That is ALL it does: the dialogue itself — copy,
// buttons, progress, close — is spoken by Agapella in her pop-up
// (`AgapellaTourPanel` inside `AgapellaWidget`), per the product decision that
// every onboarding interaction comes from the AI assistant. The widget renders
// above this layer (`WIDGET_TOUR_Z_INDEX > TOUR_OVERLAY_Z_INDEX`) so the pop-up
// is never dimmed.
//
// Steps without an anchor (welcome / completion) dim the whole page.
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { useTheme } from '../context/ThemeContext';
import { useOnboardingTour } from './OnboardingTourProvider';
import { Rect, spotlightHole, SPOTLIGHT_PAD, TOUR_OVERLAY_Z_INDEX } from './tourGeometry';

const PAD = SPOTLIGHT_PAD; // padding around the spotlighted block

function measure(node: unknown): Rect | null {
  if (Platform.OS !== 'web' || !node || typeof (node as any).getBoundingClientRect !== 'function') {
    return null;
  }
  const r = (node as HTMLElement).getBoundingClientRect();
  if (!r || (r.width === 0 && r.height === 0)) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export const OnboardingTourOverlay: React.FC = () => {
  const tour = useOnboardingTour();
  const { colors } = useTheme();
  const [rect, setRect] = useState<Rect | null>(null);

  const active = !!tour?.isActive;
  const step = tour?.step ?? null;
  const anchorKey = step?.anchor ?? null;

  const recompute = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const node = tour?.getActiveAnchorNode() ?? null;
    setRect(measure(node));
  }, [tour]);

  // Bring the anchor into view.
  //
  // scrollIntoView, not window.scrollTo: the home page scrolls inside a
  // ScrollView, so the window never scrolls and the anchor would stay off-screen
  // — leaving the page uniformly dimmed with the spotlight below the fold.
  //
  // `block: 'start'` rather than 'center' because Agapella's pop-up occupies the
  // lower-right of the viewport; parking the block high keeps it clear of her.
  const ensureVisible = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const node = tour?.getActiveAnchorNode() ?? null;
    if (node && typeof (node as any).scrollIntoView === 'function') {
      (node as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [tour]);

  // On entering a step: scroll to the block, then measure repeatedly while the
  // layout settles. The staggered re-measures also cover a block whose DOM node
  // has not mounted yet — `getActiveAnchorNode` is re-read on every tick.
  useLayoutEffect(() => {
    if (!active || Platform.OS !== 'web') return;
    ensureVisible();
    recompute();
    const timers = [120, 420, 900, 1600].map((ms) => setTimeout(recompute, ms));
    return () => timers.forEach(clearTimeout);
  }, [active, anchorKey, ensureVisible, recompute]);

  // «Мастера» and «Практики» fetch their content, so the block is often empty
  // (or absent) when the step opens and grows a second later. Without this the
  // ring keeps the stale, pre-load rectangle — which is what "the block isn't
  // highlighted" looks like: a thin strip of dimmed page, or nothing at all.
  useEffect(() => {
    if (!active || Platform.OS !== 'web' || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      recompute();
      // A block that grew below the fold has to be re-parked, or the newly
      // revealed content sits outside the viewport.
      ensureVisible();
    });
    // The node may not exist yet when the step opens (the section renders after
    // its fetch), so keep looking for it rather than giving up on the first miss.
    let attempts = 0;
    const attach = () => {
      const node = tour?.getActiveAnchorNode() as HTMLElement | null;
      if (node) {
        ro.observe(node);
        return true;
      }
      return ++attempts > 20; // ~5 s, then stop trying
    };
    let poll: ReturnType<typeof setInterval> | null = null;
    if (!attach()) {
      poll = setInterval(() => {
        if (attach() && poll) {
          clearInterval(poll);
          poll = null;
        }
      }, 250);
    }
    return () => {
      if (poll) clearInterval(poll);
      ro.disconnect();
    };
  }, [active, anchorKey, ensureVisible, recompute, tour]);

  useEffect(() => {
    if (!active || Platform.OS !== 'web') return;
    const on = () => recompute();
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [active, recompute]);

  if (!active || !step || Platform.OS !== 'web') return null;

  // While the "skip step" line is showing there is no block under discussion —
  // dim everything so the eye goes back to Agapella.
  const showSpotlight = !!anchorKey && !!rect && !tour?.transitionText;
  const hole = showSpotlight && rect ? spotlightHole(rect, PAD) : null;

  const dim = 'rgba(15, 12, 24, 0.66)';

  return (
    <View style={styles.root} pointerEvents="box-none">
      {hole ? (
        <>
          {/* Four dim panels around the spotlight hole. */}
          <View style={[styles.dim, { backgroundColor: dim, top: 0, left: 0, right: 0, height: hole.top }]} />
          <View style={[styles.dim, { backgroundColor: dim, top: hole.top + hole.height, left: 0, right: 0, bottom: 0 }]} />
          <View style={[styles.dim, { backgroundColor: dim, top: hole.top, left: 0, width: hole.left, height: hole.height }]} />
          <View style={[styles.dim, { backgroundColor: dim, top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }]} />
          {/* Highlight ring. */}
          <View
            pointerEvents="none"
            testID="agapella-tour-spotlight"
            style={[
              styles.ring,
              { top: hole.top, left: hole.left, width: hole.width, height: hole.height, borderColor: colors.solar ?? '#F5A623' },
            ]}
          />
        </>
      ) : (
        <View style={[styles.dim, StyleSheet.absoluteFillObject as object, { backgroundColor: dim }]} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // position:fixed keeps the overlay pinned while the page scrolls (web).
  root: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: TOUR_OVERLAY_Z_INDEX,
  },
  dim: { position: 'absolute' as any },
  ring: {
    position: 'absolute' as any,
    borderWidth: 2,
    borderRadius: 14,
    // subtle glow
    shadowColor: '#F5A623',
    shadowOpacity: 0.5,
    shadowRadius: 12,
  },
});
